import React, { useEffect, useState, useRef } from "react";
import { Box, Typography, Button, Snackbar } from "@mui/material";
import { keyframes } from "@emotion/react";
import confetti from "canvas-confetti";
import { Link } from "react-router-dom";
import breakpoints from "assets/theme/base/breakpoints";

// ------------------- Animations -------------------
const pulse = keyframes`
  0%,100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const gradientShift = keyframes`
  0% { background-position: 0%; }
  100% { background-position: 200%; }
`;

const float = keyframes`
  0% { transform: translateY(0) }
  50% { transform: translateY(-6px) }
  100% { transform: translateY(0) }
`;

// ---------------------------------------------------

export default function OfferBanner({ stickyMobile = false }) {
  const [timeLeft, setTimeLeft] = useState({});
  const [openToast, setOpenToast] = useState(false);
  const [confettiFired, setConfettiFired] = useState(false);
  const bannerRef = useRef(null);
  const [mobileView, setMobileView] = useState(false);

  // ----------------- Countdown Logic -------------------
  const offerEnd = new Date();
  offerEnd.setDate(offerEnd.getDate() + 3); // 3-day offer

  useEffect(() => {
    const checkView = () =>
      setMobileView(window.innerWidth < breakpoints.values.lg);
    window.addEventListener("resize", checkView);
    checkView();
    return () => window.removeEventListener("resize", checkView);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date().getTime();
      const distance = offerEnd - now;

      setTimeLeft({
        days: Math.floor(distance / (1000 * 60 * 60 * 24)),
        hours: Math.floor((distance / (1000 * 60 * 60)) % 24),
        mins: Math.floor((distance / 1000 / 60) % 60),
      });

      if (distance < 0) clearInterval(timer);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // ------------------ CONFETTI ONSCROLL ------------------
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && !confettiFired) {
          launchConfetti();
          setConfettiFired(true);
          setOpenToast(true);
        }
      },
      { threshold: 0.5 } // fires when 50% visible
    );

    if (bannerRef.current) observer.observe(bannerRef.current);

    return () => observer.disconnect();
  }, [confettiFired]);

  const launchConfetti = () => {
    const settings = {
      spread: 65,
      startVelocity: 30,
      gravity: 0.9,
      ticks: 90,
    };

    confetti({
      ...settings,
      particleCount: 60,
      angle: 60,
      origin: { x: 0.25, y: 0.7 },
    });
    confetti({
      ...settings,
      particleCount: 60,
      angle: 120,
      origin: { x: 0.75, y: 0.7 },
    });
  };

  // ------------------ Component ------------------
  return (
    <>
      <Box
        ref={bannerRef}
        sx={{
          width: stickyMobile ? "100vw" : "100%",
          maxWidth: stickyMobile ? "450px" : "100%",
          position: stickyMobile ? "fixed" : "relative",
          bottom: stickyMobile ? "15px" : "unset",
          left: stickyMobile ? "50%" : "unset",
          transform: stickyMobile ? "translateX(-50%)" : "none",
          padding: { xs: "18px", md: "28px" },
          borderRadius: "20px",
          textAlign: "center",
          color: "#fff",
          background:
            "linear-gradient(120deg, #3f6eff, #66a2ff, #5ecfff, #3f6eff)",
          backgroundSize: "200% auto",
          animation: `${gradientShift} 6s linear infinite`,
          boxShadow: "0 8px 30px rgba(0, 110, 255, 0.35)",
          overflow: "hidden",
          zIndex: stickyMobile ? 9999 : "auto",
        }}
      >
        {/* Ribbon */}
        <Box
          sx={{
            position: "absolute",
            top: "5px",
            left: "-100px",
            background: "linear-gradient(120deg, #ff5cc3, #ffb35c, #ff5cc3)",
            color: "#fff",
            fontWeight: 800,
            padding: "10px 105px",
            transform: "rotate(-20deg)",
            fontSize: "14px",
            letterSpacing: "0.5px",
            whiteSpace: "nowrap",
            borderRadius: "6px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
            display: "flex",
            alignItems: "center",
            gap: "6px",
            animation: "partyPulse 2.5s infinite ease-in-out",
            textShadow: "0 2px 6px rgba(0,0,0,0.25)",
          }}
        >
          🎉 Party Deal 🎊
        </Box>

        {/* Floating Icons */}
        <Typography
          sx={{
            position: "absolute",
            bottom: 10,
            left: 15,
            fontSize: 22,
            opacity: 0.8,
            animation: `${float} 3s infinite`,
          }}
        >
          ✨
        </Typography>
        <Typography
          sx={{
            position: "absolute",
            bottom: 10,
            right: 15,
            fontSize: 22,
            opacity: 0.8,
            animation: `${float} 3s infinite 0.3s`,
          }}
        >
          ⭐
        </Typography>

        {/* Title */}
        <Typography
          variant="h5"
          sx={{
            fontWeight: 900,
            animation: `${pulse} 2s infinite`,
            textShadow: "0 4px 12px rgba(0,0,0,0.3)",
            paddingTop:mobileView?"3rem":""
          }}
        >
          Make Your Show Go Live for Only ₹7,999!
        </Typography>

        {/* Countdown */}
        <Typography sx={{ mt: 1, opacity: 0.9, fontSize: "16px" }}>
          Offer ends in:{" "}
          <strong>
            {timeLeft.days}d : {timeLeft.hours}h : {timeLeft.mins}m
          </strong>
        </Typography>

        {/* CTA */}
        <Box mt={3} display="flex" gap={2} justifyContent="center">
          <Button
            component={Link}
            to="/search-artists"
            sx={{
              background: "#fff",
              color: "#003bca",
              fontWeight: "800",
              borderRadius: "12px",
              padding: "10px 26px",
              "&:hover": {
                background: "#e7f1ff",
                transform: "scale(1.05)",
              },
            }}
          >
            Claim Offer
          </Button>

          <Button
            href="https://wa.me/919528294837"
            target="_blank"
            sx={{
              border: "2px solid #fff",
              borderRadius: "12px",
              padding: "10px 26px",
              fontWeight: "600",
              color: "#fff",
              "&:hover": {
                background: "rgba(255,255,255,0.15)",
                transform: "scale(1.05)",
              },
            }}
          >
            WhatsApp
          </Button>
        </Box>
      </Box>

      <Snackbar
        open={openToast}
        autoHideDuration={2000}
        message="🎉 Offer Activated!"
        onClose={() => setOpenToast(false)}
      />
    </>
  );
}
